import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getReport, downloadReportPdf, shareReport } from '../api/sessions';
import { getFindings } from '../api/findings';
import { IntegrityStatusBadge } from '../components/IntegrityStatusBadge';
import { formatElapsed, questionLabel } from '../utils/timeline';
import {
  formatDuration,
  formatDateTime,
  formatTime,
  statusColor,
  severityColor,
  severityBarColor,
} from '../utils/format';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE    = 20;
const SEVERITIES   = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const BREAKDOWN_SPECIAL_KEYS = new Set([
  'confidenceScore',
  'authenticityScore',
  'behaviorRiskScore',
  'behaviorSummary',
  'behavioralAnomalies',
  'composureTrend',
  'perQuestionBreakdown',
  // P0-09 technical-failure markers: shown as a banner, never as score rows.
  'technicalFailure',
  'technicalInterruption',
  'assessmentStatus',
  'component',
  'failureCategory',
  'error',
]);

const MonacoEditor = lazy(() => import('@monaco-editor/react'));

function codingScoreClass(score) {
  if (score == null) return 'bg-slate-700 text-slate-300 border-slate-600';
  if (score >= 80) return 'bg-emerald-900/50 text-emerald-300 border-emerald-700';
  if (score >= 60) return 'bg-amber-900/50 text-amber-300 border-amber-700';
  if (score >= 40) return 'bg-orange-900/50 text-orange-300 border-orange-700';
  return 'bg-red-900/50 text-red-300 border-red-700';
}

function monacoLanguage(lang) {
  const map = { javascript: 'javascript', python: 'python', java: 'java', cpp: 'cpp' };
  return map[(lang || '').toLowerCase()] || 'javascript';
}

function formatFeedbackKey(key) {
  return String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function scoreBadgeClass(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 'bg-slate-700 text-slate-300 border-slate-600';
  const scaled = n > 10 ? n / 10 : n;
  if (scaled >= 7) return 'bg-emerald-900/50 text-emerald-300 border-emerald-700';
  if (scaled >= 4) return 'bg-amber-900/50 text-amber-300 border-amber-700';
  return 'bg-red-900/50 text-red-300 border-red-700';
}

function isNumericScore(key, value) {
  return typeof value === 'number' && /score|rating|overall/i.test(key);
}

function formatFeedbackValue(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return null;
  if (Array.isArray(value)) return value.map(String).join(' · ');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function questionGroupLabel(questionIndex) {
  if (questionIndex == null) return 'Warm-up';
  return `Q${questionIndex}`;
}

/** Group turn metrics by questionIndex (null last). */
function groupTurnMetricsByQuestion(turnMetrics) {
  const groups = new Map();
  for (const row of turnMetrics) {
    const key = row.questionIndex ?? 'warmup';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    if (a === 'warmup') return 1;
    if (b === 'warmup') return -1;
    return Number(a) - Number(b);
  });
  return keys.map((key) => ({
    questionIndex: key === 'warmup' ? null : Number(key),
    rows: groups.get(key),
  }));
}

function terminationModeLabel(reason) {
  const map = {
    COMPLETED: 'Completed normally',
    ABANDONED_NON_SERIOUS: 'Ended early (unverified abandon marker) - HR review',
    INTEGRITY_VIOLATION: 'Ended under integrity policy (unreviewed signals) - HR review',
    // Backend cannot tell an app close from a network drop here; do not blame the candidate.
    CANDIDATE_EXITED: 'App closed or connection dropped',
    ERROR_NO_AUDIO: 'No audio received (microphone or speech-to-text)',
    DISCONNECTED: 'Connection lost',
    UNKNOWN: 'Unknown',
  };
  return map[reason] || reason || 'Unknown';
}

function formatMs(ms) {
  if (ms == null) return '—';
  const n = Number(ms);
  if (Number.isNaN(n)) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}

function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(100, Math.round((Number(value) || 0) * 100)));
  const barCls = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-400 tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

function TurnMetricsTable({ turnMetrics }) {
  if (!Array.isArray(turnMetrics) || turnMetrics.length === 0) {
    return (
      <p className="text-slate-500 text-sm text-center py-4">
        No per-turn metrics recorded for this session.
      </p>
    );
  }

  const groups = groupTurnMetricsByQuestion(turnMetrics);

  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
        Objective per-turn signals
      </p>
      {groups.map((group) => (
        <div key={group.questionIndex ?? 'warmup'} className="space-y-2">
          <h4 className="text-sm font-semibold text-teal-300">
            {questionGroupLabel(group.questionIndex)}
            <span className="ml-2 text-slate-500 font-normal">
              ({group.rows.length} turn{group.rows.length !== 1 ? 's' : ''})
            </span>
          </h4>
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Turn</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase min-w-[140px]">Confidence</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">WPM</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Hedges</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Self-corr.</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Latency</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Longest pause</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {group.rows.map((row) => (
                  <tr key={row.turnIndex ?? row.id} className="hover:bg-slate-800/30 align-middle">
                    <td className="px-3 py-2 text-slate-400 tabular-nums text-xs">
                      #{(row.turnIndex ?? 0) + 1}
                    </td>
                    <td className="px-3 py-2">
                      <ConfidenceBar value={row.speakerConfidence} />
                    </td>
                    <td className="px-3 py-2 text-slate-300 tabular-nums text-xs">
                      {row.wordsPerMinute != null ? Math.round(row.wordsPerMinute) : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-300 tabular-nums text-xs">
                      {row.hedgeCount ?? 0}
                    </td>
                    <td className="px-3 py-2 text-slate-300 tabular-nums text-xs">
                      {row.selfCorrectionCount ?? 0}
                    </td>
                    <td className="px-3 py-2 text-slate-300 tabular-nums text-xs whitespace-nowrap">
                      {formatMs(row.responseLatencyMs)}
                    </td>
                    <td className="px-3 py-2 text-slate-300 tabular-nums text-xs whitespace-nowrap">
                      {formatMs(row.longestPauseMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function transcriptRoleLabel(role) {
  if (role === 'assistant') return 'Aria';
  if (role === 'candidate') return 'Candidate';
  return role || 'Unknown';
}

function groupTranscriptByQuestion(lines) {
  const groups = new Map();
  for (const line of lines) {
    const key = line.questionIndex ?? 'warmup';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(line);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    if (a === 'warmup') return 1;
    if (b === 'warmup') return -1;
    return Number(a) - Number(b);
  });
  return keys.map((key) => ({
    questionIndex: key === 'warmup' ? null : Number(key),
    rows: groups.get(key),
  }));
}

function SessionTranscriptPanel({ transcript }) {
  if (!Array.isArray(transcript) || transcript.length === 0) {
    return (
      <p className="text-slate-500 text-sm text-center py-4">
        No transcript recorded for this session.
      </p>
    );
  }

  const groups = groupTranscriptByQuestion(transcript);

  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
        Interview transcript
      </p>
      {groups.map((group) => (
        <div key={group.questionIndex ?? 'warmup'} className="space-y-3">
          <h4 className="text-sm font-semibold text-teal-300">
            {questionGroupLabel(group.questionIndex)}
          </h4>
          <div className="rounded-lg border border-slate-800 bg-slate-950/40 divide-y divide-slate-800/60">
            {group.rows.map((line) => {
              const isAssistant = line.role === 'assistant';
              return (
                <div
                  key={line.id ?? line.turnIndex}
                  className={`px-4 py-3 ${isAssistant ? 'bg-slate-900/30' : ''}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1.5">
                    <span className={isAssistant ? 'text-teal-300' : 'text-emerald-300'}>
                      {transcriptRoleLabel(line.role)}
                    </span>
                    {line.turnIndex != null && (
                      <span className="text-slate-600 font-normal normal-case ml-2">
                        turn {(line.turnIndex ?? 0) + 1}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                    {line.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// FR-003 (interviewer visibility half): what Aria used as background, and how
// the resume scored against the job description before this interview was
// ever scheduled. report.resumeUrl/atsScore/atsReason/atsStatus are only
// populated for AUTONOMOUS sessions with a linked CandidateApplication -
// supervised sessions render nothing here (not an empty state, since a
// missing resume is the normal case for that interview type, not a gap).
function CandidateResumePanel({ report }) {
  const { resumeUrl, atsScore, atsReason, atsStatus } = report ?? {};
  if (!resumeUrl && atsScore == null && !atsReason) return null;

  const atsBadgeClass =
    atsScore == null ? 'bg-slate-800 text-slate-400 border-slate-700'
    : atsScore >= 70  ? 'bg-emerald-900/50 text-emerald-300 border-emerald-700'
    : atsScore >= 40  ? 'bg-amber-900/50 text-amber-300 border-amber-700'
    :                   'bg-red-900/50 text-red-300 border-red-700';

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-3">
      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
        Candidate background (what Aria was given)
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {resumeUrl && (
          <a
            href={resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal-300 hover:text-teal-200 underline underline-offset-2"
          >
            View résumé
          </a>
        )}
        {atsScore != null && (
          <span className={`inline-flex px-2.5 py-1 rounded text-sm font-semibold border w-fit ${atsBadgeClass}`}>
            ATS match {atsScore}/100
          </span>
        )}
        {atsStatus && (
          <span className="text-xs text-slate-500 uppercase tracking-wide">{atsStatus}</span>
        )}
      </div>
      {atsReason && (
        <p className="text-sm text-slate-400 leading-relaxed">{atsReason}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryCard({ label, children, className = '' }) {
  return (
    <div className={`bg-slate-900 border border-slate-800 rounded-xl p-5 flex flex-col gap-2 ${className}`}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      {children}
    </div>
  );
}

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${colorClass}`}>
      {label}
    </span>
  );
}

/** Horizontal bar chart row for one severity level. */
function SeverityBar({ severity, count, maxCount }) {
  const pct = maxCount > 0 ? Math.max(4, Math.round((count / maxCount) * 100)) : 0;
  const barCls = severityBarColor(severity);
  const labelCls = severityColor(severity);

  return (
    <div className="flex items-center gap-3">
      <span className={`inline-flex items-center justify-center w-20 px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${labelCls}`}>
        {severity}
      </span>
      <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
        {count > 0 && (
          <div
            className={`h-full rounded-full ${barCls} transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <span className="text-sm font-semibold text-slate-300 w-6 text-right shrink-0">
        {count}
      </span>
    </div>
  );
}

/** Pretty-print a payload - try JSON parse first, fall back to raw string. */
function PayloadCell({ payload }) {
  if (!payload) return <span className="text-slate-600 italic">-</span>;

  let parsed = null;
  try {
    parsed = typeof payload === 'object' ? payload : JSON.parse(payload);
  } catch {
    // not JSON - show raw
  }

  if (parsed && typeof parsed === 'object') {
    const entries = Object.entries(parsed);
    return (
      <pre className="text-xs text-slate-300 bg-slate-800/60 rounded p-2 max-h-24 overflow-y-auto whitespace-pre-wrap break-all font-mono">
        {entries.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('\n')}
      </pre>
    );
  }

  return (
    <span className="text-xs text-slate-400 break-all">
      {String(payload)}
    </span>
  );
}

/** Client-side pagination controls. */
function Pagination({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
      <span className="text-xs text-slate-500">
        Page {page + 1} of {totalPages}
      </span>
      <div className="flex gap-2">
        <button
          onClick={onPrev}
          disabled={page === 0}
          className="px-3 py-1.5 text-xs font-medium rounded border border-slate-700
                     text-slate-300 hover:bg-slate-700 disabled:opacity-40
                     disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <button
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="px-3 py-1.5 text-xs font-medium rounded border border-slate-700
                     text-slate-300 hover:bg-slate-700 disabled:opacity-40
                     disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportPage
// ---------------------------------------------------------------------------

/**
 * ReportPage
 *
 * Route: /report/:id
 *
 * State:
 *   report          - session report object from GET /api/sessions/:id/report
 *   findings        - array from GET /api/sessions/:id/findings
 *   loading         - true while both calls are in-flight
 *   error           - string if either call fails
 *   page            - current findings table page (0-indexed)
 *   pdfLoading      - true while PDF download is in progress
 *
 * API calls:
 *   getReport(sessionId)   - on mount
 *   getFindings(sessionId) - on mount (parallel)
 *   downloadReportPdf(id)  - on "Download PDF" click
 */
// ── Integrity section (Phase 3) ───────────────────────────────────────────────
// I-P0-A: these summarise UNREVIEWED detector signals; none of them is a finding of misconduct.
const INTEGRITY_VERDICT = {
  CLEAN:        { label: 'No signals',                     cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700' },
  MINOR_FLAGS:  { label: 'Signals recorded (unreviewed)',  cls: 'bg-slate-800 text-slate-200 border-slate-600' },
  MAJOR_FLAGS:  { label: 'Signals need review',            cls: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  TERMINATED:   { label: 'Ended under integrity policy',   cls: 'bg-amber-900/50 text-amber-200 border-amber-700' },
};

// Integrity timeline time: offset from the interview start, "Time unavailable" when unknown
// (never 00:00 for a missing time). See utils/timeline.js.
const fmtMs = formatElapsed;

// Blueprint Phase 11: capability coverage per assessment-plan topic. A level is shown only
// when real evidence was graded; unreached / unanswered / ungradeable topics are
// NOT ASSESSED (never zero). Integrity never changes these rows.
const COVERAGE_LEVEL = {
  STRONG: { label: 'Strong', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  MODERATE: { label: 'Moderate', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  WEAK: { label: 'Weak', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};
const COVERAGE_REASON = {
  NOT_REACHED: 'Not reached (time, interruption or early end)',
  NO_ANSWER: 'No answer given',
  GRADING_UNAVAILABLE: 'Evaluation unavailable (technical)',
};

function TopicCoverageSection({ blueprint, rows }) {
  if (!blueprint || !Array.isArray(rows) || rows.length === 0) return null;
  const assessed = rows.filter((r) => r.status === 'ASSESSED').length;
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <div className="text-slate-200">
          <span className="font-semibold">{blueprint.name}</span>
          {blueprint.versionNo != null && <span className="text-slate-400"> · v{blueprint.versionNo}</span>}
        </div>
        <div className="text-xs text-slate-500">
          Plan {String(blueprint.assessmentPlanId || '').slice(0, 8)} · {assessed} of {rows.length} topics assessed
        </div>
      </div>
      <ul className="divide-y divide-slate-800">
        {rows.map((r) => {
          const level = COVERAGE_LEVEL[r.level];
          return (
            <li key={r.key || r.position} className="py-2.5 flex flex-wrap items-center gap-3 text-sm">
              <span className="w-6 text-slate-500 tabular-nums">{r.position}</span>
              <span className="flex-1 min-w-[160px] text-slate-200">
                {r.topic}{r.mustAsk ? <span className="text-xs text-slate-500"> (must ask)</span> : null}
                <span className="text-xs text-slate-500"> · tier {r.tier} · {r.weight}%</span>
              </span>
              {r.status === 'ASSESSED' && level ? (
                <>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${level.cls}`}>{level.label}</span>
                  {r.score != null && <span className="text-xs text-slate-400 tabular-nums w-14 text-right">{r.score} / 10</span>}
                </>
              ) : r.status === 'PENDING' ? (
                <span className="text-xs text-slate-500">Pending evaluation</span>
              ) : (
                <span className="inline-flex px-2 py-0.5 rounded text-xs border border-slate-700 text-slate-400"
                  title={COVERAGE_REASON[r.reason] || ''}>
                  Not assessed{r.reason ? ` · ${COVERAGE_REASON[r.reason] || r.reason}` : ''}
                </span>
              )}
              {r.summary && <div className="basis-full pl-9 text-xs text-slate-500">{r.summary}</div>}
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-slate-500">
        Topic coverage describes evidence per competency. The overall score above remains the canonical score.
      </p>
    </div>
  );
}

function IntegritySection({ summary }) {
  const reasons = Array.isArray(summary?.reasons) ? summary.reasons : [];
  if (!summary || ((summary.status ?? 'NONE') === 'NONE' && summary.verdict !== 'TERMINATED'
      && reasons.length === 0 && !(summary.violations || []).length)) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
        <span className="text-emerald-400 text-sm">✓ No integrity signals recorded</span>
      </div>
    );
  }

  const v = INTEGRITY_VERDICT[summary.verdict] || INTEGRITY_VERDICT.MINOR_FLAGS;
  const byType = summary.byType || {};
  const violations = Array.isArray(summary.violations) ? summary.violations : [];
  const c = summary.confrontations || null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex px-2.5 py-1 rounded text-sm font-semibold border ${v.cls}`}>
          {v.label}
        </span>
        <IntegrityStatusBadge status={summary.status} compact />
        <span className="text-xs text-slate-400">
          {summary.totalFindings ?? 0} unreviewed signal(s)
        </span>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(byType).map(([t, n]) => (
            <span key={t} className="inline-flex px-2 py-0.5 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-700">
              {t} · {n}
            </span>
          ))}
        </div>
      </div>

      {reasons.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-2">
            Reasons (server-classified; each condition counted once, however often it repeated):
          </p>
          <ul className="space-y-1.5">
            {reasons.map((r, i) => (
              <li key={i} className="text-sm text-slate-200">
                <span className="text-[11px] font-semibold text-amber-300 mr-2">{r.severity}</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-2">{r.category}</span>
                {r.message}
                <span className="ml-2 text-[11px] text-slate-500">
                  observed {r.observations}x{r.resolved ? ' · resolved' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {c && (c.warnings > 0 || c.terminated) && (
        <div className={`rounded-lg border px-3 py-2 text-sm ${c.terminated
          ? 'border-red-700 bg-red-950/40 text-red-200'
          : 'border-amber-700 bg-amber-950/30 text-amber-200'}`}>
          {c.terminated
            ? `Aria warned ${c.warnings}× about a flagged application; the interview was ended under the integrity policy. Requires review.`
            : `Aria warned ${c.warnings}× — ${c.resolved ? 'closed after warning, interview continued.' : 'see timeline.'}`}
        </div>
      )}

      {violations.length > 0 ? (
        <ol className="space-y-2">
          {violations.map((row, i) => (
            <li key={i} className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
              <span className={`tabular-nums text-xs pt-0.5 ${row.atMs == null ? 'w-24 text-slate-500 italic' : 'w-12 text-slate-400'}`}
                title={row.atSource === 'DETECTED_AT' ? 'Server receive time relative to the interview start' : undefined}>
                {fmtMs(row.atMs)}
              </span>
              {questionLabel(row.questionIndex) && (
                <span className="inline-flex px-1.5 py-0.5 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-700 whitespace-nowrap">
                  {questionLabel(row.questionIndex)}
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200">
                  {row.message || row.tool || row.type}
                  {row.repeats > 0 && (
                    <span className="ml-2 text-[11px] text-slate-500">
                      (+{row.repeats} repeat{row.repeats > 1 ? 's' : ''} within the dedupe window)
                    </span>
                  )}
                  <span className={`ml-2 inline-block w-2 h-2 rounded-full align-middle ${
                    row.severity === 'CRITICAL' || row.severity === 'HIGH' ? 'bg-red-400'
                      : row.severity === 'MEDIUM' ? 'bg-amber-400' : 'bg-slate-500'}`} />
                </div>
                {row.durationMs != null && (
                  <div className="text-[11px] text-slate-500">{(row.durationMs / 1000).toFixed(1)}s</div>
                )}
              </div>
              {row.evidenceUrl && (
                <a href={row.evidenceUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={row.evidenceUrl} alt="evidence" loading="lazy"
                       className="w-16 h-12 object-cover rounded border border-slate-700 hover:border-slate-500" />
                </a>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-slate-500 text-sm">No timeline entries.</p>
      )}
    </div>
  );
}

export default function ReportPage() {
  const { id: sessionId } = useParams();
  const navigate          = useNavigate();

  const [report,     setReport]     = useState(null);
  const [findings,   setFindings]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [page,       setPage]       = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareBusy,  setShareBusy]  = useState(false);
  const [toast,      setToast]      = useState('');

  // Auto-dismiss share toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // -- Load data on mount --
  useEffect(() => {
    let cancelled = false;
    console.log('[ReportPage] mount - loading report + findings for session', sessionId);

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [reportRes, findingsRes] = await Promise.all([
          getReport(sessionId),
          getFindings(sessionId),
        ]);
        if (cancelled) return;

        const reportData   = reportRes.data;
        // findings may come back as array or { findings: [] }
        // I-P0-B: the report's findings are the canonical server-normalized rows; fall back
        // to flattening the grouped findings API (same server classification).
        const findingsData = Array.isArray(reportData?.findings)
          ? reportData.findings
          : Array.isArray(findingsRes.data)
            ? findingsRes.data
            : Array.isArray(findingsRes.data?.groups)
              ? findingsRes.data.groups.flatMap((g) => (g.items || []).map((i) => ({ ...i, description: i.message })))
              : (findingsRes.data?.findings ?? findingsRes.data?.content ?? []);

        console.log('[ReportPage] loaded report:', reportData?.sessionCode, '| findings:', findingsData.length);
        setReport(reportData);
        setFindings(findingsData);
      } catch (err) {
        if (cancelled) return;
        console.error('[ReportPage] load failed:', err.message);
        setError(err.message || 'Failed to load report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [sessionId]);

  // -- PDF download --
  const handleDownloadPdf = useCallback(async () => {
    setPdfLoading(true);
    console.log('[ReportPage] downloading PDF for session', sessionId);
    try {
      const res  = await downloadReportPdf(sessionId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `report-${report?.sessionCode ?? sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('[ReportPage] PDF download triggered');
    } catch (err) {
      console.error('[ReportPage] PDF download failed:', err.message);
      alert(`PDF download failed: ${err.message}`);
    } finally {
      setPdfLoading(false);
    }
  }, [sessionId, report]);

  // -- Share link (Phase 8) --
  const handleShare = useCallback(async () => {
    setShareBusy(true);
    try {
      const res = await shareReport(sessionId);
      const url = res.data?.url;
      if (url && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setToast('Public share link copied to clipboard');
      } else if (url) {
        setToast(url);
      }
      console.log('[ReportPage] share link issued', url);
    } catch (err) {
      console.error('[ReportPage] share failed:', err.message);
      setToast(`Share failed: ${err.message}`);
    } finally {
      setShareBusy(false);
    }
  }, [sessionId]);

  // -- Derived values --
  const severityCounts = SEVERITIES.reduce((acc, s) => {
    acc[s] = findings.filter(f => (f.severity || '').toUpperCase() === s).length;
    return acc;
  }, {});
  const maxCount    = Math.max(1, ...Object.values(severityCounts));
  const totalPages  = Math.ceil(findings.length / PAGE_SIZE);
  const pageSlice   = findings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // -- Loading state --
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading report...</p>
        </div>
      </div>
    );
  }

  // -- Error state --
  if (error) {
    return (
      <div className="flex items-center justify-center py-24 px-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md text-center space-y-3">
          <p className="text-red-700 font-semibold">Failed to load report</p>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/reports')}
            className="btn btn-primary text-sm"
          >
            Back to reports
          </button>
        </div>
      </div>
    );
  }

  const sessionCode = report?.sessionCode ?? report?.code ?? sessionId;
  // I-P0-B: server-derived integrity status (NONE / MEDIUM / HIGH). There is no numeric risk
  // score and no signal-activity chart any more.
  const integrityStatus = report?.integrityStatus ?? report?.integritySummary?.status ?? null;

  // Sticky-left-nav section list - the seven sections required by Phase 8 spec.
  const NAV_SECTIONS = [
    { id: 'overview',    label: 'Overview' },
    { id: 'findings',    label: 'Integrity Signals' },
    { id: 'code',        label: 'Code' },
    { id: 'questions',   label: 'Questions & Scores' },
    { id: 'breakdown',   label: 'Interview Breakdown' },
    { id: 'integrity',   label: 'Integrity' },
    { id: 'coding',      label: 'Coding Round' },
    { id: 'scorecard',   label: 'Scorecard' },
    { id: 'notes',       label: 'Notes' },
  ];

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full">

      {report?.terminationReason === 'ABANDONED_NON_SERIOUS' && (
        <div
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
        >
          Interview ended early on an unverified abandon marker. No conclusion about the candidate
          has been made. Flagged for HR review.
        </div>
      )}

      {report?.terminationReason === 'INTEGRITY_VIOLATION' && (
        <div
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
        >
          Interview ended under the integrity policy after an automated signal (an application
          matching a listed tool, or a capture-protected window) was reported. The signal is
          unreviewed and is not a finding of misconduct. See the integrity timeline. Flagged for
          HR review.
        </div>
      )}

      {report?.interviewFeedback?.technicalFailure === true && (
        <div
          className="mb-6 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800"
          role="alert"
        >
          Not assessed - a technical issue prevented scoring. This is not a candidate result.
          {report.requiresHrReview ? ' Flagged for HR review.' : ''}
        </div>
      )}

      {report?.interviewIncomplete &&
        report?.terminationReason !== 'ABANDONED_NON_SERIOUS' &&
        report?.terminationReason !== 'INTEGRITY_VIOLATION' && (
        <div
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
        >
          {report.interviewFeedback?.technicalInterruption === true
            ? 'Interview interrupted by a technical or unclear ending (not a candidate outcome) - '
            : 'Interview incomplete - '}
          reached {report.questionsReached ?? 0} of {report.questionsTotal ?? 0}{' '}
          questions. Ended via {terminationModeLabel(report.terminationReason)}.
          {report.interviewFeedback?.technicalInterruption === true ? ' Scored only on the questions reached.' : ''}
          {report.requiresHrReview ? ' Flagged for HR review.' : ''}
        </div>
      )}

      <div className="card p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <Link
              to="/reports"
              className="text-xs font-medium text-teal-600 hover:text-teal-700"
            >
              ← Reports
            </Link>
            <p className="text-xs text-slate-600 font-mono truncate mt-1.5">#{sessionCode}</p>
            {report?.startedAt && (
              <p className="text-xs text-slate-500 mt-1">
                Started {formatDateTime(report.startedAt)}
              </p>
            )}
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleShare}
              disabled={shareBusy}
              className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-200 bg-white
                         text-emerald-800 hover:bg-emerald-50 disabled:opacity-50
                         text-sm font-semibold rounded-lg transition-colors"
            >
              {shareBusy ? 'Generating...' : 'Share'}
            </button>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="btn btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pdfLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Downloading...
                </>
              ) : (
                'Download PDF'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Two-column: sticky nav + scroll content */}
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8">

        {/* ── Sticky left nav ─────────────────────────────────────── */}
        <nav className="hidden lg:block sticky top-6 self-start">
          <ul className="space-y-1 text-sm">
            {NAV_SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}
                   className="block px-3 py-1.5 rounded-md text-ink-secondary hover:text-teal-700 hover:bg-teal-50/70 transition-colors">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* ── Scrollable content ──────────────────────────────────── */}
        <div className="space-y-10 min-w-0">

        {/* Section 1: Overview */}
        <section id="overview" className="space-y-6 scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Overview</h2>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SummaryCard label="Integrity Signals (unreviewed)">
              <p className="text-4xl font-bold text-white">{findings.length}</p>
            </SummaryCard>

            <SummaryCard label="Integrity Status">
              <IntegrityStatusBadge status={integrityStatus} />
            </SummaryCard>

            <SummaryCard label="Duration">
              <p className="text-2xl font-bold text-white">
                {formatDuration(report?.durationSeconds)}
              </p>
            </SummaryCard>

            <SummaryCard label="Status">
              <Badge
                label={report?.status ?? '-'}
                colorClass={statusColor(report?.status)}
              />
            </SummaryCard>
          </div>

          {/* Candidate & Host Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl px-5 py-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Host</p>
              <p className="text-sm text-slate-200">
                {report?.hostName && <span className="font-medium">{report.hostName} - </span>}
                {report?.hostEmail ?? report?.createdBy ?? '-'}
              </p>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl px-5 py-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Candidate</p>
              <p className="text-sm text-slate-200">
                {report?.candidateName && <span className="font-medium">{report.candidateName} - </span>}
                {report?.candidateEmail ?? (
                  <span className="text-slate-600 italic">Not joined</span>
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Findings (severity bars + paginated table) */}
        <section id="findings" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Integrity Signals by Severity (unreviewed)
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Integrity signals are automated, unreviewed detector observations. They can be caused by
            legitimate software, hardware or environment and are not a finding of misconduct.
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4 mb-6">
            {SEVERITIES.map((sev) => (
              <SeverityBar
                key={sev}
                severity={sev}
                count={severityCounts[sev]}
                maxCount={maxCount}
              />
            ))}
          </div>

          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Findings Timeline
            <span className="ml-2 text-slate-500 normal-case font-normal">
              ({findings.length} total)
            </span>
          </h3>

          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            {findings.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-slate-500 text-sm">No findings recorded for this session.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-10">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-28">Severity</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-28">Detected At</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {pageSlice.map((f, idx) => (
                        <tr key={f.id ?? idx} className="hover:bg-slate-800/30 transition-colors align-top">
                          <td className="px-4 py-3 text-slate-500 text-xs tabular-nums">
                            {page * PAGE_SIZE + idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              label={f.severity ?? 'UNKNOWN'}
                              colorClass={severityColor(f.severity)}
                            />
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-xs font-mono whitespace-nowrap">
                            {formatTime(f.detectedAt ?? f.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            {f.description ? (
                              <div className="text-xs text-slate-300">
                                <span>{f.description}</span>
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                                  {[f.integrityCategory, f.resolved ? 'resolved' : null,
                                    f.reviewRequired ? 'needs review' : 'context only']
                                    .filter(Boolean).join(' · ')}
                                </span>
                              </div>
                            ) : (
                              <PayloadCell payload={f.details ?? f.payload ?? f.data} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPrev={() => { setPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  onNext={() => { setPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              </>
            )}
          </div>
        </section>

        {/* Section 4: Code snapshot */}
        <section id="code" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Code</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            {report?.lastCodeSnapshot ? (
              <div>
                {report.codeLanguage && (
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                    {report.codeLanguage}
                  </p>
                )}
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4
                                text-xs text-slate-300 font-mono overflow-x-auto max-h-96">
                  <code>{report.lastCodeSnapshot}</code>
                </pre>
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-6">
                No code snapshot was captured for this session.
              </p>
            )}
          </div>
        </section>

        {report?.blueprint && (
          <section id="topic-coverage" className="scroll-mt-6">
            <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
              Topic Coverage
            </h2>
            <TopicCoverageSection blueprint={report.blueprint} rows={report.topicCoverage} />
          </section>
        )}

        {/* Section 5: Questions & Scores */}
        <section id="questions" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Questions &amp; Scores
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            {Array.isArray(report?.interviewFeedback?.perQuestionBreakdown)
              && report.interviewFeedback.perQuestionBreakdown.length > 0 ? (
              <ul className="divide-y divide-slate-800">
                {report.interviewFeedback.perQuestionBreakdown.map((q, i) => (
                  <li key={q.questionIndex ?? i} className="py-3 space-y-1">
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-200">
                        Q{q.questionIndex ?? i + 1}: {q.title ?? `Question ${i + 1}`}
                      </span>
                      <span className={`text-sm font-mono whitespace-nowrap px-2 py-0.5 rounded border ${scoreBadgeClass(q.score)}`}>
                        {q.score != null ? `${q.score}/10` : '—'}
                      </span>
                    </div>
                    {q.note && <p className="text-xs text-slate-500">{q.note}</p>}
                  </li>
                ))}
              </ul>
            ) : Array.isArray(report?.questionScores) && report.questionScores.length > 0 ? (
              <ul className="divide-y divide-slate-800">
                {report.questionScores.map((q, i) => (
                  <li key={q.id ?? i} className="py-3 flex items-start justify-between gap-4">
                    <span className="text-sm text-slate-200">{q.prompt ?? q.title ?? `Question ${i + 1}`}</span>
                    <span className="text-sm text-teal-300 font-mono whitespace-nowrap">
                      {q.score != null ? `${q.score}/${q.maxScore ?? 5}` : '-'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm text-center py-6">
                No question scoring was recorded.
              </p>
            )}
          </div>
        </section>

        {/* Section: Interview Breakdown (autonomous AI scoring) */}
        <section id="breakdown" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Interview Breakdown
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            {!report?.interviewFeedback || Object.keys(report.interviewFeedback).length === 0
              || report.interviewFeedback.technicalFailure === true ? (
              <p className="text-slate-500 text-sm text-center py-6 px-4">
                {report?.interviewFeedback?.technicalFailure === true
                  ? 'Not assessed - a technical issue prevented scoring. No breakdown was produced.'
                  : 'Breakdown not available for this session'}
              </p>
            ) : (
              <div className="p-4 sm:p-6 space-y-6">
                <CandidateResumePanel report={report} />

                {(report.interviewFeedback?.confidenceScore != null
                  || report.interviewFeedback?.authenticityScore != null
                  || report.interviewFeedback?.behaviorRiskScore != null) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {report.interviewFeedback?.confidenceScore != null && (
                      <SummaryCard label="Confidence">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded text-sm font-semibold border w-fit ${scoreBadgeClass(report.interviewFeedback.confidenceScore)}`}
                        >
                          {report.interviewFeedback.confidenceScore}
                        </span>
                      </SummaryCard>
                    )}
                    {report.interviewFeedback?.authenticityScore != null && (
                      <SummaryCard label="Authenticity (legacy model guess, unvalidated)">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded text-sm font-semibold border w-fit ${scoreBadgeClass(report.interviewFeedback.authenticityScore)}`}
                        >
                          {report.interviewFeedback.authenticityScore}
                        </span>
                      </SummaryCard>
                    )}
                    {report.interviewFeedback?.behaviorRiskScore != null && (
                      <SummaryCard label="AI-assistance guess (legacy, unvalidated)">
                        {/* I-P0-A: older sessions only; a model guess from speech rhythm, never evidence. */}
                        <span className="inline-flex px-2.5 py-1 rounded text-sm font-semibold border w-fit bg-slate-800 text-slate-300 border-slate-600">
                          {report.interviewFeedback.behaviorRiskScore}
                        </span>
                      </SummaryCard>
                    )}
                  </div>
                )}

                {report.interviewFeedback?.composureTrend && (
                  <p className="text-sm text-slate-400">
                    Composure trajectory:{' '}
                    <span className="text-emerald-300 font-medium capitalize">
                      {report.interviewFeedback.composureTrend}
                    </span>
                    <span className="text-slate-600 ml-1">(talent signal)</span>
                  </p>
                )}

                {report.interviewFeedback?.behaviorSummary && (
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {report.interviewFeedback.behaviorSummary}
                  </p>
                )}

                <SessionTranscriptPanel transcript={report.transcript} />

                <TurnMetricsTable turnMetrics={report.turnMetrics} />

                {Array.isArray(report.interviewFeedback?.behavioralAnomalies)
                  && report.interviewFeedback.behavioralAnomalies.length > 0 && (
                  <details className="bg-slate-950/60 border border-slate-800 rounded-lg">
                    <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-200 hover:text-white">
                      Speech-pattern observations (unvalidated, not integrity evidence)
                      <span className="ml-2 text-slate-500 font-normal">
                        ({report.interviewFeedback.behavioralAnomalies.length})
                      </span>
                    </summary>
                    <div className="border-t border-slate-800 divide-y divide-slate-800/60">
                      {report.interviewFeedback.behavioralAnomalies.map((anomaly, idx) => {
                        const utterance = anomaly?.utteranceText ?? '';
                        const truncated = utterance.length > 80
                          ? `${utterance.slice(0, 80)}...`
                          : utterance;
                        const flags = Array.isArray(anomaly?.flags)
                          ? anomaly.flags.join(', ')
                          : String(anomaly?.flags ?? '');
                        return (
                          <div key={idx} className="px-4 py-3 text-sm space-y-1">
                            <p className="text-slate-200">
                              <span className="font-semibold text-amber-300">
                                Score {anomaly?.anomalyScore ?? '—'}
                              </span>
                              {flags ? ` · ${flags}` : ''}
                            </p>
                            {truncated && (
                              <p className="text-slate-500 text-xs italic">&ldquo;{truncated}&rdquo;</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}

                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Area
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider w-28">
                        Score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Feedback
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {Object.entries(report.interviewFeedback)
                      .filter(([key]) => !BREAKDOWN_SPECIAL_KEYS.has(key))
                      .map(([key, value]) => {
                      const text = formatFeedbackValue(value);
                      const showScore = isNumericScore(key, value);
                      if (!showScore && !text) return null;
                      return (
                        <tr key={key} className="hover:bg-slate-800/30 align-top">
                          <td className="px-4 py-3 text-slate-200 font-medium">
                            {formatFeedbackKey(key)}
                          </td>
                          <td className="px-4 py-3">
                            {showScore ? (
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${scoreBadgeClass(value)}`}
                              >
                                {value}
                              </span>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-sm whitespace-pre-wrap">
                            {text ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Section: Integrity (Phase 3 — proctor + evidence chain) */}
        <section id="integrity" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Integrity
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Integrity signals are automated, unreviewed detector observations. They can be caused by
            legitimate software, hardware or environment and are not a finding of misconduct.
          </p>
          <IntegritySection summary={report?.integritySummary} />
        </section>

        {/* Section: Coding Round */}
        <section id="coding" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
            Coding Round
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
            {!report?.codingSubmission || Object.keys(report.codingSubmission).length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-6">
                Coding round not completed
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-teal-900/50 text-teal-300 border border-teal-700 capitalize">
                    {(report.codingSubmission.language || 'code').toString()}
                  </span>
                  {report.codingScore != null && (
                    <span className={`inline-flex px-2.5 py-0.5 rounded text-xs font-bold border ${codingScoreClass(report.codingScore)}`}>
                      Score: {Math.round(report.codingScore)}
                    </span>
                  )}
                  {report.codingFeedback?.verdict && (
                    <span className="text-xs text-slate-400 uppercase tracking-wide">
                      {report.codingFeedback.verdict}
                    </span>
                  )}
                </div>
                <div className="rounded-lg border border-slate-700 overflow-hidden" style={{ height: '320px' }}>
                  <Suspense fallback={<p className="text-slate-500 text-sm p-4">Loading editor…</p>}>
                    <MonacoEditor
                      height="100%"
                      language={monacoLanguage(report.codingSubmission.language)}
                      value={String(report.codingSubmission.code || '')}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 13,
                        scrollBeyondLastLine: false,
                      }}
                    />
                  </Suspense>
                </div>
                {report.codingFeedback && (
                  <div className="space-y-3 text-sm">
                    {report.codingFeedback.technicalFailure === true && (
                      <p className="text-slate-400 italic">
                        Coding evaluation not completed - a technical issue prevented grading. This is not a candidate result.
                      </p>
                    )}
                    {report.codingFeedback.summary && (
                      <p className="text-slate-300">{report.codingFeedback.summary}</p>
                    )}
                    {Array.isArray(report.codingFeedback.strengths) && report.codingFeedback.strengths.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-emerald-400 uppercase mb-1">Strengths</p>
                        <ul className="list-disc list-inside text-slate-400">
                          {report.codingFeedback.strengths.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(report.codingFeedback.concerns) && report.codingFeedback.concerns.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-amber-400 uppercase mb-1">Areas to improve</p>
                        <ul className="list-disc list-inside text-slate-400">
                          {report.codingFeedback.concerns.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Section 6: Scorecard */}
        <section id="scorecard" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Scorecard</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            {report?.scorecard && Object.keys(report.scorecard).length > 0 ? (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {Object.entries(report.scorecard).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-slate-800 pb-1">
                    <dt className="text-slate-400 capitalize">{k.replace(/[_-]/g, ' ')}</dt>
                    <dd className="text-slate-200 font-mono">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-slate-500 text-sm text-center py-6">
                Interviewer did not save a scorecard for this session.
              </p>
            )}
            {report?.verdictSummary && (
              <p className="text-slate-300 text-sm mt-4 italic border-l-2 border-teal-500 pl-3">
                {report.verdictSummary}
              </p>
            )}
          </div>
        </section>

        {/* Section 7: Notes */}
        <section id="notes" className="scroll-mt-6">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Notes</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            {report?.notes ? (
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{report.notes}</p>
            ) : (
              <p className="text-slate-500 text-sm text-center py-6">
                No interviewer notes recorded.
              </p>
            )}
          </div>
        </section>

        </div>{/* end content column */}
      </div>{/* end grid */}

      {/* Footer */}
      <footer className="pt-6 mt-10 border-t border-slate-800">
        <p className="text-center text-xs text-slate-500">
          Samastix - Integrity Monitoring
        </p>
      </footer>

      {toast && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-slate-900 text-white text-sm rounded-lg shadow-lg z-50 border border-slate-700">
          {toast}
        </div>
      )}

    </div>
  );
}
